import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { VideoStill } from './VideoStill';
import { Shutter } from './motion';
import { CDN } from '../lib/cdn';

const ease = [0.16, 1, 0.3, 1] as const;

const FEATURES = [
  { n: 'Causal Reasoning', title: 'Reasons across cause and effect', body: 'Every shot is planned in full context — who\'s in frame, what just happened, what has to follow next.', meta: [['∞', 'context retained'], ['1', 'continuous timeline']], video: CDN.features.prompt, reverse: false },
  { n: 'Verification', title: 'Nothing reaches the cut unverified', body: 'A closed loop checks every generated shot against story causality before it lands on the director\'s timeline.', meta: [['100%', 'shots verified'], ['0', 'continuity errors']], video: CDN.features.memory, reverse: true },
  { n: 'Constitution', title: 'A Cinematic Constitution, enforced', body: 'Framing, eyelines, screen direction, continuity of light — the grammar of cinema, enforced automatically.', meta: [['every', 'shot checked'], ['auto', 'enforced grammar']], video: CDN.features.synthesis, reverse: false },
  { n: 'Model Routing', title: 'Best model for every shot', body: 'Kling, Seedance, Veo, Flux — treated as swappable engines. When the next checkpoint ships, you just get better shots.', meta: [['4', 'swappable engines'], ['best', 'model per shot']], video: CDN.features.cinematography, reverse: true },
  { n: 'Shot Control', title: 'Override without breaking the cut', body: 'Change one shot — angle, lens, performance. Induce re-verifies the neighboring shots instead of re-rolling the film.', meta: [['Δ', 'local, not global'], ['01', 'shot at a time']], video: CDN.features.director, reverse: false },
];

const textVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};
const lineVariant = {
  hidden: { opacity: 0, y: 36 },
  visible: { opacity: 1, y: 0, transition: { duration: 1, ease } },
};

function FeatureItem({ f, first }: { f: (typeof FEATURES)[number]; first?: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['8%', '-8%']);

  return (
    <div ref={containerRef} className={`grid grid-cols-2 gap-20 items-center py-24 relative max-lg:grid-cols-1 max-lg:gap-10 max-lg:py-16 ${first ? 'pt-16 max-lg:pt-10' : ''}`}>
      {/* On mobile: text first, image last regardless of reverse */}
      <div className={`f-still max-lg:order-2 ${f.reverse ? 'lg:order-2' : ''}`}>
        <motion.div
          className="letterbox vignette relative w-full overflow-hidden bg-black"
          style={{ y: imgY, willChange: 'transform' }}
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-120px' }}
          transition={{ duration: 1.2, ease }}
        >
          <Shutter label={`FEATURE / ${f.n}`} className="absolute inset-0" aspect="2.4 / 1">
            <VideoStill
              src={f.video}
              className="feature-video absolute inset-0 h-full w-full object-cover"
              aria-label={`Induce feature demo: ${f.title}`}
            />
          </Shutter>
        </motion.div>
      </div>

      <motion.div
        className={`f-text max-lg:order-1 ${f.reverse ? 'lg:order-1' : ''}`}
        variants={textVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
      >
        <motion.div variants={lineVariant} className="font-mono text-[11px] tracking-[0.22em] text-on-surface-muted uppercase inline-flex items-center gap-[10px] mb-5">
          <span className="w-[30px] h-px bg-primary" /> {f.n}
        </motion.div>
        <motion.h3 variants={lineVariant} className="mkt-feature-title text-[clamp(28px,3vw,52px)] leading-[1.04] tracking-[-0.02em] m-0 mb-[18px] text-on-surface [text-wrap:balance]">
          {f.title}
        </motion.h3>
        <motion.p variants={lineVariant} className="text-[17px] text-on-surface-variant leading-[1.55] max-w-[48ch] m-0 mb-6 [text-wrap:pretty]">
          {f.body}
        </motion.p>
        <motion.div variants={lineVariant} className="flex gap-7 mt-7 pt-6">
          {f.meta.map(([v, k], idx) => (
            <div key={idx} className="font-mono text-[10px] tracking-[0.14em] uppercase text-on-surface-muted">
              <b className="block text-on-surface text-[22px] tracking-[-0.01em] normal-case font-display font-medium mb-1">{v}</b>
              {k}
            </div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
}

export function Features() {
  return (
    <section className="section band-light low" id="product">
      <div className="container">
        <motion.div
          className="grid [grid-template-columns:auto_1fr] items-end mb-6 max-lg:grid-cols-1 max-lg:gap-6"
          initial={{ opacity: 0, y: 44 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 1, ease }}
        >
          <h2 className="mkt-section-title home-section-title text-[clamp(30px,5.4vw,84px)] leading-[0.98] m-0 max-w-[20ch]">
            Built like a <em>film crew.</em>
            <br />
            Runs like an infrastructure.
          </h2>
        </motion.div>

        {FEATURES.map((f, i) => <FeatureItem key={f.n} f={f} first={i === 0} />)}
      </div>
    </section>
  );
}
